import {
  Box,
  Skeleton,
  Stack,
  Typography,
  type TypographyProps,
  styled,
  useTheme,
} from "@mui/material"

import type { PropsWithChildren } from "react"
import { EvolutionStatChip } from "../../dataDisplay"
import { SafeApiComponent } from "../SafeApiComponent"

interface FinancialSummaryCardSlotProps {
  title?: TypographyProps
  subtitle?: TypographyProps
  kpi?: TypographyProps
}

export interface FinancialSummaryCardProps {
  title: string
  subtitle: string
  kpi?: string
  evol?: number | null
  hideEvolution?: boolean
  slotProps?: FinancialSummaryCardSlotProps
}

export const FinancialSummaryCard = ({
  title,
  subtitle,
  kpi,
  evol,
  hideEvolution,
  slotProps,
}: FinancialSummaryCardProps) => {
  const { palette } = useTheme()
  const subtitleColor = palette.common.electricBlue

  return (
    <Stack gap={0.75} alignItems="center" flex={1}>
      <Title {...slotProps?.title}>{title}</Title>
      <Subtitle color={subtitleColor} {...slotProps?.subtitle}>
        {subtitle}
      </Subtitle>
      <Kpi {...slotProps?.kpi}>{kpi}</Kpi>
      <EvolutionStatChip
        arrow
        bold
        border={false}
        evol={evol}
        noValueLabel={hideEvolution ? "" : undefined}
      />
    </Stack>
  )
}

export const LazyFinancialSummaryCard = ({
  children,
  ...props
}: PropsWithChildren<Pick<FinancialSummaryCardProps, "title" | "subtitle">>) => {
  return (
    <SafeApiComponent
      errorBoundary={{
        fallbackRender: () => <FinancialSummaryCard {...props} />,
      }}
      suspense={{
        fallback: (
          <Box flex={1}>
            <Skeleton variant="rectangular" height="123px" />
          </Box>
        ),
      }}
    >
      {children}
    </SafeApiComponent>
  )
}

const Title = styled(Typography)(({ theme: { typography } }) => ({
  fontWeight: 600,
  textTransform: "uppercase",
  fontSize: typography.pxToRem(18),
}))

const Subtitle = styled(Typography)(({ theme: { typography } }) => ({
  fontWeight: 500,
  fontSize: typography.pxToRem(12),
  textAlign: "center",
}))

const Kpi = styled(Typography)(({ theme: { typography } }) => ({
  fontWeight: 600,
  fontSize: typography.pxToRem(24),
}))
